import axios from "axios";

// 배송 사진 업로드는 기사님이 현장에서 전파가 약한 곳에 있는 경우가 많아
// JSON 요청용 기본 타임아웃(10초)으로는 정상적인 업로드도 실패로 끊긴다.
const UPLOAD_TIMEOUT = 60000;

// 기본 API 인스턴스 생성
const api = axios.create({
  baseURL: 'https://admin.xn--wv4b09focz31b.com/',
  // baseURL: 'http://192.168.0.104:8000/',
  // baseURL: "https://admin.cookerp.shop/",
  timeout: 10000, // 10초 타임아웃 (네트워크 상태가 좋지 않을 경우 대비)
  headers: {
    "Content-Type": "application/json",
  },
});

// 액세스 토큰이 필요한 요청에 자동으로 헤더 추가
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 토큰 자체를 발급/갱신하는 요청은 401이 나도 재시도 대상이 아니다.
// (아이디·비밀번호 오류나 리프레시 토큰 만료를 무한 재시도하게 된다)
const NO_RETRY_PATHS = ["users/driver-app-login/", "users/refresh-token/"];

// 동시에 여러 요청이 401을 받아도 갱신은 한 번만 수행한다
let refreshPromise = null;

function refreshAccessTokenOnce() {
  if (!refreshPromise) {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      return Promise.reject(new Error("리프레시 토큰이 없습니다"));
    }

    refreshPromise = refreshAccessToken(refreshToken)
      .then((response) => {
        if (!response?.access_token) {
          throw new Error("토큰 갱신 응답 형식이 올바르지 않습니다");
        }
        tokenUtils.setTokens(response.access_token, response.refresh_token);
        return response.access_token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// 액세스 토큰 만료(401) 시 갱신 후 원 요청을 한 번 재시도한다.
// 이 처리가 없으면 기사님이 배송 화면에 머무는 동안 토큰이 만료됐을 때
// 남은 근무 시간 내내 목록 조회와 사진 전송이 실패한다. 갱신은 로그인
// 화면이 마운트될 때만 실행되기 때문이다.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const isUnauthorized = error.response?.status === 401;
    const isRetryable =
      isUnauthorized &&
      config &&
      !config._retriedAfterRefresh &&
      !NO_RETRY_PATHS.some((path) => (config.url || "").includes(path));

    if (!isRetryable) {
      return Promise.reject(error);
    }

    config._retriedAfterRefresh = true;

    try {
      await refreshAccessTokenOnce();
      // 재시도 시 요청 인터셉터가 갱신된 토큰으로 헤더를 다시 채운다
      return await api(config);
    } catch (refreshError) {
      console.error("토큰 갱신 실패, 로그인 화면으로 이동합니다:", refreshError);
      tokenUtils.clearTokens();
      if (window.location.pathname !== "/") {
        // QR 딥링크(?delivery_id=...) 등 원래 가려던 주소를 로그인 화면에
        // 넘겨서, 로그인 후 일반 목록이 아닌 원래 목적지로 복귀하게 한다
        const returnTo = window.location.pathname + window.location.search;
        window.location.assign("/?next=" + encodeURIComponent(returnTo));
      }
      return Promise.reject(error);
    }
  }
);

/**
 * 사용자 로그인
 * @param {string} username - 사용자 아이디
 * @param {string} password - 사용자 비밀번호
 * @returns {Promise<Object>} 토큰 및 사용자 정보
 */
export async function login(username, password) {
  try {
    const response = await api.post("users/driver-app-login/", { username, password });
    return response.data;
  } catch (error) {
    console.error("로그인 오류:", error);
    throw error;
  }
}

/**
 * 사용자 정보 조회
 * @param {string} accessToken - 액세스 토큰
 * @returns {Promise<Object>} 사용자 정보
 */
export async function getUserInfo(accessToken) {
  try {
    const response = await api.get("users/driver-ck-token/", {
      params: { access_token: accessToken },
    });
    return response.data;
  } catch (error) {
    console.error("사용자 정보 가져오기 오류:", error);
    throw error;
  }
}

/**
 * 액세스 토큰 갱신
 * @param {string} refreshToken - 리프레시 토큰
 * @returns {Promise<Object>} 새로운 토큰 정보
 */
export async function refreshAccessToken(refreshToken) {
  try {
    const response = await api.post("users/refresh-token/", {
      refresh_token: refreshToken,
    });
    console.log("새로운 액세스 토큰:", response.data);
    return response.data;
  } catch (error) {
    console.error("토큰 갱신 오류:", error);
    throw error;
  }
}

/**
 * 배송 주문 목록 조회
 * @param {Object} params - 검색 파라미터 (page, delivery_date, driver 등)
 * @returns {Promise<Object>} 주문 목록 데이터
 */
export async function getOrders(params) {
  try {
    const response = await api.get("order/get-order-by-driver/", { params });

    return response.data;
  } catch (error) {
    console.error("주문 정보 가져오기 오류:", error);
    throw error;
  }
}

/**
 * 배송묶음 목록 조회 (배송예정일 기준)
 * @param {Object} params - { page, delivery_date }
 * @returns {Promise<Object>} { count, next, total_delivered_count, results }
 */
export async function getDeliveryGroups(params) {
  try {
    const response = await api.get("order/delivery-groups/by-driver/", { params });

    return response.data;
  } catch (error) {
    console.error("배송묶음 조회 오류:", error);
    throw error;
  }
}

/**
 * 배송묶음 단위 배송완료 처리
 * 사진은 묶음당 1장만 전송하고, 묶음에 연결된 주문들은 서버에서 그 이미지를 참조한다.
 * @param {string} delivery_id - 배송묶음 UUID
 * @param {File} image - 배송 완료 이미지 (촬영시각 워터마크 포함)
 * @param {string} captured_at - 촬영 시각 ISO 8601 (KST 오프셋 포함)
 */
export async function completeDeliveryGroup({ delivery_id, image, captured_at }) {
  try {
    const formData = new FormData();
    formData.append("image", image);
    formData.append("captured_at", captured_at);

    const response = await api.post(`order/delivery-groups/${delivery_id}/complete/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: UPLOAD_TIMEOUT,
    });
    return response.data;
  } catch (error) {
    console.error("배송묶음 완료 처리 오류:", error);

    throw error;
  }
}

/**
 * 배송 완료 상태로 변경 (구버전 - 주문 단위)
 * @param {string} order_id - 주문 id
 * @param {File} image - 배송 완료 이미지
 */
export async function changeDeliveryStatus({ order_id, image }) {
  try {
    console.log("배송 상태 변경 요청:", order_id, image);

    const formData = new FormData();
    formData.append("order_id", order_id);
    formData.append("image", image);

    const response = await api.post("order/order-delivery-image/upload/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: UPLOAD_TIMEOUT,
    });
    return response.data;
  } catch (error) {
    console.error("배송 상태 변경 오류:", error);

    throw error;
  }
}

/**
 * 토큰 관련 유틸리티 함수들
 */
export const tokenUtils = {
  // 토큰 저장
  setTokens(accessToken, refreshToken) {
    localStorage.setItem("accessToken", accessToken);
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }
  },

  // 토큰 제거 (로그아웃)
  clearTokens() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("driverName");

    return true;
  },

  // 토큰 존재 여부 확인
  hasAccessToken() {
    return !!localStorage.getItem("accessToken");
  },

  hasRefreshToken() {
    return !!localStorage.getItem("refreshToken");
  },
};
